import React from 'react'
import { Box, Typography } from '@mui/material'
import ReactMarkdown from 'react-markdown'
import { ChatMessage } from '../../../hooks/useChatAssistant'
import { extractUserText } from './utils/draftEnvelope'
import CopilotToolChip from './CopilotToolChip'
import CopilotDraftCard from './CopilotDraftCard'
import { DraftCardInfo } from './hooks/useCopilotSession'
import { CopilotDraftHost } from './types'

const markdownSx = {
  fontSize: '0.85rem',
  lineHeight: 1.5,
  wordBreak: 'break-word' as const,
  '& p': { m: 0, mb: 0.75 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { m: 0, mb: 0.75, pl: 2.5 },
  '& code': {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '0.78rem',
    backgroundColor: 'background.custom3',
    borderRadius: 0.5,
    px: 0.5,
  },
  '& pre': {
    backgroundColor: 'background.custom3',
    borderRadius: 1,
    p: 1,
    overflowX: 'auto' as const,
  },
}

interface CopilotMessageProps {
  message: ChatMessage
  draftCards: Record<string, DraftCardInfo>
  host: CopilotDraftHost
}

// Memoized: earlier messages keep object identity while the streaming message
// is replaced per token — without memo every prior message re-parses its full
// markdown AST on every delta
const CopilotMessage: React.FC<CopilotMessageProps> = React.memo(function CopilotMessage({
  message,
  draftCards,
  host,
}) {
  if (message.role === 'user') {
    return (
      <Box display="flex" justifyContent="flex-end">
        <Box
          sx={{
            backgroundColor: 'background.custom3',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            maxWidth: '85%',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {extractUserText(message.content)}
          </Typography>
        </Box>
      </Box>
    )
  }

  // Assistant: ordered text + inline tool segments (falls back to plain
  // content for restored history, which has no segments)
  const segments = message.segments
  return (
    <Box display="flex" flexDirection="column" gap={0.5} sx={{ maxWidth: '95%' }}>
      {segments ? (
        segments.map((segment, index) =>
          segment.type === 'text' ? (
            segment.text.trim() ? (
              <Box key={index} sx={markdownSx}>
                <ReactMarkdown>{segment.text}</ReactMarkdown>
              </Box>
            ) : null
          ) : (
            <Box key={index} display="flex" flexDirection="column" gap={0.5}>
              <CopilotToolChip segment={segment} />
              {draftCards[segment.toolCallId] && (
                <CopilotDraftCard
                  card={draftCards[segment.toolCallId]}
                  toolCallId={segment.toolCallId}
                  host={host}
                />
              )}
            </Box>
          )
        )
      ) : message.content.trim() ? (
        <Box sx={markdownSx}>
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </Box>
      ) : null}
    </Box>
  )
})

export default CopilotMessage
